/* ============================================================
   Salty Seats - Warranty and returns page (ss-wr-* sections)

   Four jobs:
     1. the two doors, which decide which half of the page shows
     2. the 30 day eligibility checker
     3. the product group switcher (condition panel + coverage panel)
     4. the claim builder, which writes the email and posts it
        through Shopify's native contact form

   Everything is delegated off the document rather than bound per
   section, because the sections are independent in the theme editor
   and any one of them can be added, removed or reordered.
   ============================================================ */
(function () {
  'use strict';

  var ROOT = document.documentElement;

  /* Sections come and go in the theme editor, so every init is
     guarded. Without this a section reload binds a second set of
     listeners and every click counts twice. */
  var docBound = false;

  function once(el) {
    if (!el || el.dataset.sswrInit === '1') return false;
    el.dataset.sswrInit = '1';
    return true;
  }

  /* ---------- the two doors ---------- */

  function currentDoor() {
    return ROOT.getAttribute('data-sswr-active') === 'fault' ? 'fault' : 'mind';
  }

  function paintDoors() {
    var door = currentDoor();
    document.querySelectorAll('[data-sswr-doorbtn]').forEach(function (btn) {
      var on = btn.getAttribute('data-sswr-doorbtn') === door;
      btn.setAttribute('aria-pressed', on ? 'true' : 'false');
      var label = btn.querySelector('[data-sswr-doorlabel]');
      if (label) {
        label.textContent = on
          ? btn.getAttribute('data-sswr-on-label') || ''
          : btn.getAttribute('data-sswr-off-label') || '';
      }
      var arrow = btn.querySelector('[data-sswr-doorarrow]');
      if (arrow) arrow.textContent = on ? '↓' : '→';
    });
  }

  function setDoor(door, fromHash) {
    ROOT.setAttribute('data-sswr-active', door === 'fault' ? 'fault' : 'mind');
    paintDoors();
    if (!fromHash && window.history && window.history.replaceState) {
      window.history.replaceState(null, '', '#' + door);
    }
  }

  function initDoors() {
    if (docBound) {
      paintDoors();
      return;
    }
    docBound = true;

    document.addEventListener('click', function (e) {
      var btn = e.target.closest && e.target.closest('[data-sswr-doorbtn]');
      if (!btn) return;
      e.preventDefault();
      setDoor(btn.getAttribute('data-sswr-doorbtn'), false);
      var doors = btn.closest('.sswr-doors');
      if (doors) doors.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });

    window.addEventListener('hashchange', function () {
      var h = (window.location.hash || '').replace('#', '').toLowerCase();
      if (h === 'fault' || h === 'claim' || h === 'warranty') setDoor('fault', true);
      else if (h === 'mind' || h === 'return' || h === 'returns') setDoor('mind', true);
    });

    paintDoors();
  }

  /* ---------- 30 day eligibility checker ---------- */

  var MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  function fmt(d) {
    return d.getDate() + ' ' + MONTHS[d.getMonth()] + ' ' + d.getFullYear();
  }

  function fill(tpl, map) {
    return String(tpl || '').replace(/\[(\w+)\]/g, function (whole, key) {
      return Object.prototype.hasOwnProperty.call(map, key) ? map[key] : whole;
    });
  }

  function paintWindow(box) {
    var input = box.querySelector('[data-sswr-date]');
    var card = box.querySelector('[data-sswr-verdict]');
    var title = box.querySelector('[data-sswr-verdict-t]');
    var body = box.querySelector('[data-sswr-verdict-d]');
    if (!input || !card || !title || !body) return;

    var days = parseInt(box.getAttribute('data-sswr-days'), 10);
    if (isNaN(days) || days < 1) days = 30;

    card.classList.remove('sswr-verdict--ok', 'sswr-verdict--no');

    var raw = input.value;
    if (!raw) {
      title.textContent = box.getAttribute('data-t-waiting-t') || '';
      body.textContent = box.getAttribute('data-t-waiting-d') || '';
      return;
    }

    var parts = raw.split('-');
    var d = new Date(+parts[0], +parts[1] - 1, +parts[2]);
    if (isNaN(d.getTime())) {
      title.textContent = box.getAttribute('data-t-waiting-t') || '';
      body.textContent = box.getAttribute('data-t-waiting-d') || '';
      return;
    }

    var today = new Date();
    today.setHours(0, 0, 0, 0);
    var elapsed = Math.floor((today - d) / 86400000);
    var due = new Date(d.getTime());
    due.setDate(due.getDate() + days);

    if (elapsed < 0) {
      card.classList.add('sswr-verdict--no');
      title.textContent = box.getAttribute('data-t-future-t') || '';
      body.textContent = box.getAttribute('data-t-future-d') || '';
      return;
    }

    if (elapsed <= days) {
      var left = days - elapsed;
      card.classList.add('sswr-verdict--ok');
      title.textContent = fill(
        left === 1 ? box.getAttribute('data-t-in-t-one') : box.getAttribute('data-t-in-t'),
        { n: left }
      );
      body.textContent = fill(box.getAttribute('data-t-in-d'), {
        n: left,
        days: days,
        date: fmt(due)
      });
      return;
    }

    card.classList.add('sswr-verdict--no');
    title.textContent = fill(box.getAttribute('data-t-out-t'), { days: days });
    body.textContent = fill(box.getAttribute('data-t-out-d'), {
      n: elapsed,
      days: days,
      date: fmt(due)
    });
  }

  function initWindow() {
    document.querySelectorAll('[data-sswr-window]').forEach(function (box) {
      if (!once(box)) return;
      var input = box.querySelector('[data-sswr-date]');
      if (!input) return;
      input.addEventListener('input', function () { paintWindow(box); });
      input.addEventListener('change', function () { paintWindow(box); });
      paintWindow(box);
    });
  }

  /* ---------- product groups ---------- */

  /* Read once. The section that owns the group blocks writes this,
     and the claim builder and the coverage panel both read it, so
     the wording cannot drift between the three places it appears. */
  function groupData() {
    var el = document.querySelector('[data-sswr-groups]');
    if (!el) return { shared: [], groups: [] };
    try {
      var parsed = JSON.parse(el.textContent);
      return {
        shared: parsed.shared || [],
        groups: parsed.groups || []
      };
    } catch (err) {
      /* A malformed payload is a bug worth seeing, not one to hide. */
      if (window.console && console.warn) console.warn('ss-warranty: could not read group data', err);
      return { shared: [], groups: [] };
    }
  }

  function showPanel(scope, attr, id) {
    scope.querySelectorAll('[' + attr + ']').forEach(function (panel) {
      panel.hidden = panel.getAttribute(attr) !== id;
    });
  }

  function initCondition() {
    document.querySelectorAll('[data-sswr-cond]').forEach(function (box) {
      if (!once(box)) return;
      var chips = box.querySelectorAll('[data-sswr-chip]');
      if (!chips.length) return;

      function pick(id) {
        chips.forEach(function (chip) {
          chip.setAttribute('aria-pressed', chip.getAttribute('data-sswr-chip') === id ? 'true' : 'false');
        });
        showPanel(box, 'data-sswr-condpanel', id);
      }

      chips.forEach(function (chip) {
        chip.addEventListener('click', function () { pick(chip.getAttribute('data-sswr-chip')); });
      });

      pick(chips[0].getAttribute('data-sswr-chip'));
    });
  }

  /* ---------- claim builder ---------- */

  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function initClaim() {
    var box = document.querySelector('[data-sswr-claim]');
    if (!box || !once(box)) return;

    var data = groupData();
    var groups = data.groups;
    /* Shopify's {% form %} tag renders the element, so it is found by
       the class the tag was given rather than a data attribute. */
    var form = box.querySelector('form.sswr-claim__formwrap') || box.querySelector('form');
    var tiles = box.querySelector('[data-sswr-producttiles]');
    var faults = box.querySelector('[data-sswr-faultchips]');
    var preview = box.querySelector('[data-sswr-preview]');
    var send = box.querySelector('[data-sswr-send]');
    var ready = box.querySelector('[data-sswr-ready]');
    var copyBtn = box.querySelector('[data-sswr-copy]');
    var orderInput = box.querySelector('[data-sswr-order]');
    var emailInput = box.querySelector('[data-sswr-email]');
    var nameInput = box.querySelector('[data-sswr-name]');
    var noteInput = box.querySelector('[data-sswr-note]');

    /* photos is keyed by tile id. In checklist mode a record is just
       {ticked:true}; with an endpoint it carries the upload's progress
       and, once done, the link that goes in the email. */
    var state = {
      group: groups.length ? groups[0].id : '',
      faults: {},
      photos: {},
      ref: 'SS-W' + (1000 + Math.floor(Math.random() * 9000))
    };

    var endpoint = box.getAttribute('data-sswr-upload') || '';
    var maxBytes = (parseFloat(box.getAttribute('data-sswr-maxmb')) || 15) * 1024 * 1024;

    function current() {
      for (var i = 0; i < groups.length; i++) if (groups[i].id === state.group) return groups[i];
      return groups[0] || { id: '', name: '', covers: [] };
    }

    /* Product tiles, built from the same blocks the rest of the page
       uses. No tiles when the groups section is missing - the form
       still works, it just asks in the free-text box instead. */
    function buildTiles() {
      if (!tiles || !groups.length) return;
      tiles.innerHTML = groups.map(function (g) {
        var img = g.image
          ? '<span class="sswr-tile__img"><img src="' + esc(g.image) + '" alt="" loading="lazy" width="40" height="40"></span>'
          : '';
        return '<button type="button" class="sswr-tile" data-sswr-product="' + esc(g.id) + '" aria-pressed="false">' +
          img + '<span class="sswr-tile__n">' + esc(g.name) + '</span></button>';
      }).join('');
    }

    function faultOptions() {
      var g = current();
      var list = [];
      if (data.shared.length) list.push(data.shared[0]);
      list = list.concat(g.covers || []);
      var other = box.getAttribute('data-t-other');
      if (other) list.push(other);
      return list;
    }

    function buildFaults() {
      if (!faults) return;
      faults.innerHTML = faultOptions().map(function (t) {
        return '<button type="button" class="sswr-fault" data-sswr-fault="' + esc(t) + '" aria-pressed="' +
          (state.faults[t] ? 'true' : 'false') + '">' + esc(t) + '</button>';
      }).join('');
    }

    function picked() {
      return faultOptions().filter(function (t) { return state.faults[t]; });
    }

    function photoRecords() {
      var out = [];
      box.querySelectorAll('[data-sswr-photo]').forEach(function (p) {
        var rec = state.photos[p.getAttribute('data-sswr-photo')];
        if (rec) out.push(rec);
      });
      return out;
    }

    function photoCount() {
      return photoRecords().filter(function (r) {
        return endpoint ? r.status === 'done' : r.ticked;
      }).length;
    }

    function photoLinks() {
      return photoRecords()
        .filter(function (r) { return r.status === 'done' && r.url; })
        .map(function (r) { return r.url; });
    }

    function uploading() {
      return photoRecords().some(function (r) { return r.status === 'uploading'; });
    }

    /* A required tile is only meaningful when there is somewhere to
       upload to. Without an endpoint the tick is a promise, not a
       photo, so it is not something to hold a claim on. */
    function missingPhotos() {
      if (!endpoint) return false;
      var missing = false;
      box.querySelectorAll('[data-sswr-photo][data-sswr-required]').forEach(function (tile) {
        var rec = state.photos[tile.getAttribute('data-sswr-photo')];
        if (!rec || rec.status !== 'done') missing = true;
      });
      return missing;
    }

    function t(key, fallback) {
      var v = box.getAttribute('data-t-' + key);
      return v == null || v === '' ? fallback : v;
    }

    function compose() {
      var g = current();
      var order = orderInput ? orderInput.value.trim() : '';
      var note = noteInput ? noteInput.value.trim() : '';
      var mail = emailInput ? emailInput.value.trim() : '';
      var chosen = picked();
      var count = photoCount();
      var to = box.getAttribute('data-sswr-to') || '';

      var lines = [];
      if (to) lines.push('To: ' + to);
      lines.push('Subject: ' + t('subject', 'Warranty claim') + ' - ' + g.name +
        (order ? ' - ' + t('subjectorder', 'Order') + ' ' + order : ''));
      lines.push('');
      lines.push(t('greeting', 'Hi Salty Seats,'));
      lines.push('');
      lines.push(t('intro', 'I would like to make a warranty claim on my') + ' ' + g.name + '.');
      lines.push('');
      lines.push(t('wrong', 'What went wrong:'));
      if (chosen.length) {
        chosen.forEach(function (c) { lines.push('  - ' + c); });
      } else {
        lines.push('  - ' + t('wrongempty', '(pick what went wrong above)'));
      }
      if (note) {
        lines.push('');
        lines.push(t('else', 'Anything else:'));
        note.split('\n').forEach(function (l) { lines.push('  ' + l); });
      }
      lines.push('');
      lines.push(t('order', 'Order number:') + ' ' + (order || t('orderempty', '(add your order number)')));

      var links = photoLinks();
      if (links.length) {
        lines.push(t('photos', 'Photos:') + ' ' + fill(t('photolist', '[n] attached'), { n: links.length }));
        links.forEach(function (u) { lines.push('  ' + u); });
      } else {
        lines.push(t('photos', 'Photos:') + ' ' + (count || t('photosempty', 'none yet')));
      }
      if (mail) lines.push(t('replyto', 'Reply to:') + ' ' + mail);
      lines.push(t('reference', 'Reference:') + ' ' + state.ref);
      lines.push('');
      lines.push(t('thanks', 'Thanks,'));
      return lines.join('\n');
    }

    function outstanding() {
      var need = [];
      if (!(emailInput && emailInput.value.trim())) need.push(t('needemail', ''));
      if (!(orderInput && orderInput.value.trim())) need.push(t('needorder', ''));
      if (!picked().length) need.push(t('needfault', ''));
      if (missingPhotos()) need.push(t('needphotos', ''));
      return need.filter(Boolean);
    }

    function isReady() {
      return outstanding().length === 0;
    }

    /* "a, b and c" - so the prompt names only what is actually left
       rather than reciting the whole form back at someone who has
       already filled most of it in. */
    function listOut(items) {
      if (items.length < 2) return items[0] || '';
      var word = t('needand', 'and');
      return items.slice(0, -1).join(', ') + ' ' + word + ' ' + items[items.length - 1];
    }

    function paint() {
      var g = current();

      box.querySelectorAll('[data-sswr-product]').forEach(function (tile) {
        tile.setAttribute('aria-pressed', tile.getAttribute('data-sswr-product') === state.group ? 'true' : 'false');
      });
      box.querySelectorAll('[data-sswr-fault]').forEach(function (chip) {
        chip.setAttribute('aria-pressed', state.faults[chip.getAttribute('data-sswr-fault')] ? 'true' : 'false');
      });
      box.querySelectorAll('[data-sswr-photo]').forEach(paintPhoto);

      document.querySelectorAll('[data-sswr-fname]').forEach(function (el) { el.textContent = g.name; });
      showPanel(document, 'data-sswr-coverpanel', g.id);

      var body = compose();
      if (preview) preview.textContent = body;

      /* An upload still in flight blocks the send, or the email would
         go without the link to the photo the customer just chose. */
      var busy = uploading();
      var need = outstanding();
      var ok = need.length === 0 && !busy;
      if (send) send.disabled = !ok;
      if (ready) {
        ready.textContent = busy
          ? t('readybusy', '')
          : (need.length ? fill(t('readyno', ''), { list: listOut(need) }) : t('readyyes', ''));
      }

      if (form) {
        setHidden('Reference', state.ref);
        setHidden('Product', g.name);
        setHidden('What went wrong', picked().join('; '));
        var links = photoLinks();
        setHidden('Photos', links.length ? links.join('\n') : String(photoCount()));
        var bodyField = form.querySelector('[name="contact[body]"]');
        if (bodyField) bodyField.value = body;
      }
    }

    function setHidden(key, value) {
      if (!form) return;
      var field = form.querySelector('[name="contact[' + key + ']"]');
      if (field) field.value = value;
    }

    /* ---------- photo uploads ----------
       One request per file, so a slow photo does not hold up a fast
       one and each tile can report its own outcome. */

    function paintPhoto(tile) {
      var id = tile.getAttribute('data-sswr-photo');
      var rec = state.photos[id];
      var mark = tile.querySelector('[data-sswr-photomark]');
      var done = tile.querySelector('[data-sswr-photodone]');
      var err = tile.querySelector('[data-sswr-photoerror]');

      if (!endpoint) {
        var on = !!(rec && rec.ticked);
        tile.setAttribute('aria-pressed', on ? 'true' : 'false');
        if (mark) {
          mark.textContent = on
            ? tile.getAttribute('data-sswr-on-label') || ''
            : tile.getAttribute('data-sswr-off-label') || '';
        }
        return;
      }

      var status = (rec && rec.status) || 'empty';
      tile.setAttribute('data-state', status);

      if (mark) {
        mark.textContent =
          status === 'uploading' ? tile.getAttribute('data-sswr-busy-label') || ''
          : status === 'done' ? tile.getAttribute('data-sswr-on-label') || ''
          : tile.getAttribute('data-sswr-add-label') || '';
      }

      if (done) {
        done.hidden = status !== 'done';
        var name = done.querySelector('[data-sswr-photoname]');
        if (name) name.textContent = (rec && rec.name) || '';
        var thumb = done.querySelector('[data-sswr-photothumb]');
        if (thumb) {
          if (status === 'done' && rec.preview) {
            thumb.src = rec.preview;
            thumb.hidden = false;
          } else {
            thumb.removeAttribute('src');
            thumb.hidden = true;
          }
        }
      }

      if (err) {
        var message = (rec && rec.error) || '';
        err.textContent = message;
        err.hidden = !message;
      }
    }

    function clearPhoto(tile) {
      if (!tile) return;
      var id = tile.getAttribute('data-sswr-photo');
      var rec = state.photos[id];
      if (rec && rec.preview) URL.revokeObjectURL(rec.preview);
      state.photos[id] = null;
      var input = tile.querySelector('[data-sswr-file]');
      if (input) input.value = '';
      paint();
    }

    function looksLikeAPhoto(file) {
      if (/^image\//i.test(file.type || '')) return true;
      /* Some browsers hand over an empty type for HEIC, so the name is
         the fallback rather than the first check. */
      return /\.(jpe?g|png|webp|heic|heif)$/i.test(file.name || '');
    }

    function failPhoto(tile, id, message) {
      state.photos[id] = { status: 'error', error: message };
      paint();
      var input = tile.querySelector('[data-sswr-file]');
      if (input) input.value = '';
    }

    function sendPhoto(tile, file) {
      var id = tile.getAttribute('data-sswr-photo');

      if (!looksLikeAPhoto(file)) {
        failPhoto(tile, id, t('errtype', ''));
        return;
      }
      if (file.size > maxBytes) {
        failPhoto(tile, id, fill(t('errsize', ''), { n: Math.round(maxBytes / 1048576) }));
        return;
      }

      var preview = '';
      try {
        preview = URL.createObjectURL(file);
      } catch (e) {
        preview = '';
      }

      state.photos[id] = { status: 'uploading', name: file.name, preview: preview };
      paint();

      var body = new FormData();
      body.append('file', file);
      body.append('reference', state.ref);
      body.append('slot', tile.getAttribute('data-sswr-slot') || 'photo');

      fetch(endpoint, { method: 'POST', body: body })
        .then(function (res) {
          return res.json().then(
            function (data) { return { ok: res.ok, data: data }; },
            function () { return { ok: false, data: {} }; }
          );
        })
        .then(function (result) {
          if (result.ok && result.data && result.data.ok && result.data.url) {
            state.photos[id] = {
              status: 'done',
              url: result.data.url,
              name: file.name,
              preview: preview
            };
          } else {
            if (preview) URL.revokeObjectURL(preview);
            /* The endpoint's own words when it gave any, because "that
               photo is over 15MB" is worth more than "upload failed". */
            state.photos[id] = {
              status: 'error',
              error: (result.data && result.data.error) || t('errfailed', '')
            };
          }
          paint();
        })
        .catch(function (err) {
          if (preview) URL.revokeObjectURL(preview);
          state.photos[id] = {
            status: 'error',
            error: t('errfailed', '') || String((err && err.message) || err)
          };
          paint();
        });
    }

    if (endpoint) {
      box.querySelectorAll('[data-sswr-file]').forEach(function (input) {
        input.addEventListener('change', function () {
          var tile = input.closest('[data-sswr-photo]');
          var file = input.files && input.files[0];
          if (tile && file) sendPhoto(tile, file);
        });
      });
    }

    box.addEventListener('click', function (e) {
      var tile = e.target.closest && e.target.closest('[data-sswr-product]');
      if (tile) {
        state.group = tile.getAttribute('data-sswr-product');
        state.faults = {};
        buildFaults();
        paint();
        return;
      }
      var chip = e.target.closest && e.target.closest('[data-sswr-fault]');
      if (chip) {
        var key = chip.getAttribute('data-sswr-fault');
        state.faults[key] = !state.faults[key];
        paint();
        return;
      }
      var remove = e.target.closest && e.target.closest('[data-sswr-photoremove]');
      if (remove) {
        var rt = remove.closest('[data-sswr-photo]');
        clearPhoto(rt);
        return;
      }
      /* With an uploader the tile is a label wrapping a file input, so
         the browser opens the picker and there is nothing to toggle. */
      if (endpoint) return;
      var photo = e.target.closest && e.target.closest('[data-sswr-photo]');
      if (photo) {
        var pk = photo.getAttribute('data-sswr-photo');
        state.photos[pk] = state.photos[pk] ? null : { ticked: true };
        paint();
      }
    });

    [orderInput, emailInput, nameInput, noteInput].forEach(function (el) {
      if (el) el.addEventListener('input', paint);
    });

    if (copyBtn) {
      var copyTimer = null;
      copyBtn.addEventListener('click', function () {
        var text = compose();
        var done = function () {
          copyBtn.setAttribute('data-copied', '1');
          copyBtn.textContent = copyBtn.getAttribute('data-sswr-on-label') || '';
          clearTimeout(copyTimer);
          copyTimer = setTimeout(function () {
            copyBtn.removeAttribute('data-copied');
            copyBtn.textContent = copyBtn.getAttribute('data-sswr-off-label') || '';
          }, 2000);
        };
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(text).then(done, function () { done(); });
        } else {
          done();
        }
      });
    }

    /* Submit over fetch and swap the card in place. The page never
       reloads on a send it can read the answer to - a reload throws
       away the reference, the uploaded photos and everything typed,
       and replaces it with a card that cannot name any of them.

       The one exception is Shopify's bot challenge, which genuinely
       needs a real navigation to solve. Everything else, success or
       refusal, is reported here. */
    var fail = box.querySelector('[data-sswr-sendfail]');

    function showDone() {
      var panel = box.querySelector('[data-sswr-claimpanel]');
      var done = box.querySelector('[data-sswr-done]');
      box.querySelectorAll('[data-sswr-ref]').forEach(function (el) { el.textContent = state.ref; });
      box.querySelectorAll('[data-sswr-donename]').forEach(function (el) { el.textContent = current().name; });
      box.querySelectorAll('[data-sswr-refline]').forEach(function (el) { el.hidden = false; });
      if (panel) panel.hidden = true;
      if (done) {
        done.hidden = false;
        done.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }

    function showFail(detail) {
      if (!fail) return;
      var text = t('sendfailed', '');
      fail.textContent = detail ? text + ' (' + detail + ')' : text;
      fail.hidden = false;
    }

    /* Written before the send, so the reference survives the one path
       that still navigates. */
    function remember() {
      try {
        window.sessionStorage.setItem(
          'sswr-claim',
          JSON.stringify({ ref: state.ref, name: current().name })
        );
      } catch (err) {
        /* Private browsing refuses this. Not worth failing a claim over. */
      }
    }

    if (form) {
      form.addEventListener('submit', function (e) {
        if (typeof form.checkValidity === 'function' && !form.checkValidity()) return;
        if (!window.fetch || !window.FormData) return;
        e.preventDefault();
        if (fail) fail.hidden = true;
        remember();
        var label = send ? send.textContent : '';
        if (send) {
          send.disabled = true;
          send.textContent = send.getAttribute('data-sending') || label;
        }
        var restore = function () {
          if (send) { send.disabled = false; send.textContent = label; }
        };
        fetch(form.getAttribute('action') || '/contact', {
          method: 'POST',
          body: new FormData(form),
          headers: { Accept: 'text/html' }
        })
          .then(function (res) {
            if (res.url && res.url.indexOf('challenge') !== -1) {
              /* A captcha cannot be answered from here. */
              restore();
              form.submit();
              return;
            }
            if (res.ok) {
              showDone();
            } else {
              restore();
              showFail(res.status ? 'HTTP ' + res.status : '');
            }
          })
          .catch(function (err) {
            /* Same origin, so a throw here is the network, not CORS.
               Retrying beats reloading: a reload would lose the lot
               and probably fail the same way. */
            restore();
            showFail(String((err && err.message) || ''));
          });
      });
    }

    buildTiles();
    buildFaults();
    paint();
  }

  /* After a native post the page reloads at the top. Open the fault
     door, bring the visitor back to the result, and put the reference
     back on the card - the server cannot know it, but the tab can. */
  function initPosted() {
    var flag = document.querySelector('[data-sswr-posted]');
    if (!flag) return;
    setDoor('fault', true);

    var head = document.querySelector('[data-sswr-donehead]');
    if (head) {
      var saved = null;
      try {
        saved = JSON.parse(window.sessionStorage.getItem('sswr-claim') || 'null');
      } catch (err) {
        saved = null;
      }
      if (saved && saved.ref) {
        var lead = head.getAttribute('data-sswr-withref');
        if (lead) head.textContent = lead + ' ' + saved.ref + '.';
        document.querySelectorAll('[data-sswr-ref]').forEach(function (el) {
          el.textContent = saved.ref;
        });
        document.querySelectorAll('[data-sswr-donename]').forEach(function (el) {
          el.textContent = saved.name || '';
        });
        var meta = document.querySelector('[data-sswr-donemeta]');
        if (meta && saved.name) meta.hidden = false;
      }
      try {
        window.sessionStorage.removeItem('sswr-claim');
      } catch (err) {
        /* nothing to do */
      }
    }

    setTimeout(function () {
      flag.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 60);
  }

  function boot() {
    if (!document.querySelector('.sswr')) return;
    initDoors();
    initWindow();
    initCondition();
    initClaim();
    initPosted();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  /* Re-run when the theme editor swaps a section back in. */
  document.addEventListener('shopify:section:load', function (e) {
    if (e.target && e.target.querySelector && e.target.querySelector('.sswr')) boot();
  });
})();
